'use client'

import {X} from 'lucide-react'
import {useIntl} from 'react-intl'
import {Badge} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import type {ProductFacetDefinition} from '@/components/products/ProductFacetSidebar'
import {cn} from '@/lib/utils'

/**
 * Removable chips for the currently-applied catalog filters (JO2-64).
 *
 * The facet sidebar shows what *can* be selected; this shows what *is*, above
 * the results, so a visitor can see and undo a filter without reopening the
 * sidebar — which on mobile is behind a sheet.
 *
 * Values come from the same `definitions` the sidebar renders, so the two
 * cannot drift: removing a chip calls the same `onRemove` the sidebar's
 * checkbox calls.
 */

type ActiveFilterBadgesProps<TFilterKey extends string = string> = {
  definitions: ProductFacetDefinition<TFilterKey>[]
  onRemove: (key: TFilterKey, value: string) => void
  onClearAll: () => void
  className?: string
}

export function ActiveFilterBadges<TFilterKey extends string = string>({
  definitions,
  onRemove,
  onClearAll,
  className,
}: ActiveFilterBadgesProps<TFilterKey>) {
  const intl = useIntl()

  const active = definitions.flatMap((definition) =>
    definition.values.map((value) => ({
      key: definition.key,
      value,
      // Fall back to the raw value when a selection has no matching option —
      // e.g. a filter carried in from the URL for a facet that this site does
      // not offer. Showing it is what makes it removable.
      label: definition.options.find((option) => option.value === value)?.label ?? value,
      groupLabel: definition.label,
    })),
  )

  if (!active.length) return null

  return (
    <div
      className={cn('flex flex-wrap items-center gap-site-xs', className)}
      data-testid="active-filter-badges"
    >
      {active.map(({key, value, label, groupLabel}) => (
        <Badge key={`${key}:${value}`} variant="secondary" className="h-7 gap-1 pr-1 pl-2.5">
          <span className="u-body-xs">{label}</span>
          <button
            type="button"
            onClick={() => onRemove(key, value)}
            aria-label={intl.formatMessage(
              {id: 'catalog.removeFilter', defaultMessage: 'Remove filter {label}'},
              {label: `${groupLabel}: ${label}`},
            )}
            className="inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-foreground/10 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            <X aria-hidden="true" className="size-3" />
          </button>
        </Badge>
      ))}

      {active.length > 1 ? (
        <Button variant="ghost" size="sm" onClick={onClearAll} className="h-7 px-2">
          {intl.formatMessage({id: 'catalog.clearFilters', defaultMessage: 'Clear filters'})}
        </Button>
      ) : null}
    </div>
  )
}
